import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from './api';
import { orgPath } from './nav';

export interface Openable {
  customerOrgId: string;
  tenantId: string;
  label: string;
  environment: string;
  status: string;
  identityId: string;
  username: string;
  endsAt: string | null;
  lastOpenedAt?: string;
}

/**
 * Opening an org, wherever the button happens to be.
 *
 * The home page offers the two or three orgs somebody actually works in and the full list offers the rest,
 * but opening one is the same act either way, and it is the act with the consequences: a real Salesforce
 * session, sometimes a ticket reference, sometimes a second factor. Keeping it in one place means those
 * prompts cannot end up asked on one screen and skipped on the other.
 */
export function useOpenSession(onError: (message: string) => void) {
  const [busy, setBusy] = useState<string | null>(null);
  const [prompt, setPrompt] = useState<{ org: Openable; needTicket: boolean; needMfa: boolean } | null>(null);

  async function open(org: Openable, extra?: { ticketRef?: string; reason?: string; totp?: string }) {
    setBusy(org.customerOrgId + org.identityId);
    try {
      const r = await api<{ channel: string; frontdoorUri: string }>('/api/access', {
        method: 'POST',
        body: { customerOrgId: org.customerOrgId, identityId: org.identityId, channel: 'browser', ...extra },
      });
      setPrompt(null);
      window.location.href = r.frontdoorUri; // single-use Salesforce login URL
    } catch (err) {
      // A missing reason or second factor is not a failure, it is the next question. Anything else is.
      if (err instanceof ApiError && (err.code === 'reason_required' || err.code === 'mfa_required')) {
        setPrompt({ org, needTicket: err.code === 'reason_required', needMfa: err.code === 'mfa_required' });
      } else {
        onError(err instanceof ApiError ? err.message : 'Could not open that org');
      }
    } finally {
      setBusy(null);
    }
  }

  return { open, busy, prompt, dismissPrompt: () => setPrompt(null) };
}

const envLabel = (environment: string) => (environment === 'sandbox' ? 'Sandbox' : 'Production');

/**
 * The questions asked before a session is opened: a ticket and reason, a second factor, or both.
 *
 * Callers put it near the top of the page rather than after the list, because the list can be long and
 * the button that raised it may be the last one on it. The first field takes focus, which also scrolls it
 * into view, so the answer to "why did nothing happen?" is in front of the person who clicked.
 */
export function OpenPrompt({
  prompt,
  onSubmit,
  onCancel,
}: {
  prompt: { org: Openable; needTicket: boolean; needMfa: boolean };
  onSubmit: (extra: { ticketRef?: string; reason?: string; totp?: string }) => void;
  onCancel: () => void;
}) {
  const [ticket, setTicket] = useState('');
  const [reason, setReason] = useState('');
  const [totp, setTotp] = useState('');
  const { org } = prompt;
  return (
    <section className="card open-prompt" aria-label={`Before opening ${org.label}`}>
      <h2>Before opening {org.label}</h2>
      {/* Which org and which login, restated: production and sandbox often share a name, and this is the
          last screen before a real session exists. */}
      <div className="tile-meta" style={{ marginBottom: 16 }}>
        <span className={`env-tag ${org.environment}`}>{envLabel(org.environment)}</span>
        <span>as {org.username}</span>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({ ticketRef: ticket || undefined, reason: reason || undefined, totp: totp || undefined });
        }}
      >
        {prompt.needTicket && (
          <>
            <label>
              Ticket reference
              <input value={ticket} onChange={(e) => setTicket(e.target.value)} required placeholder="ISM-1234" autoFocus />
            </label>
            <label>
              Reason
              <input value={reason} onChange={(e) => setReason(e.target.value)} required placeholder="What you need to do" />
            </label>
          </>
        )}
        {prompt.needMfa && (
          <label>
            Authenticator code
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, ''))}
              required
              autoFocus={!prompt.needTicket}
            />
          </label>
        )}
        <div className="actions">
          <button type="submit" className="block">Open in Salesforce</button>
          <button type="button" className="secondary block" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </section>
  );
}

/**
 * One openable org as a tile with its Open button. Used by the short list and the long one alike, and laid
 * out in a .tiles grid by whoever lists them.
 *
 * The name links to the org's own pages, so the tile is also a way into the org rather than only out of it
 * to Salesforce. The environment is a tag rather than a word in a sentence, because opening production when
 * you meant the sandbox is the mistake this page is most likely to cause.
 */
export function OrgTile({
  org,
  busy,
  onOpen,
  note,
  workspace,
}: {
  org: Openable;
  busy: string | null;
  onOpen: () => void;
  note?: string;
  /** The workspace's name, for lists that mix workspaces without grouping them. */
  workspace?: string;
}) {
  const active = org.status === 'active';
  const opening = busy === org.customerOrgId + org.identityId;
  return (
    <div className="tile">
      <div className="tile-title">
        <Link to={orgPath(org.tenantId, org.customerOrgId)}>{org.label}</Link>
      </div>
      <div className="tile-meta">
        <span className={`env-tag ${org.environment}`}>{envLabel(org.environment)}</span>
        {!active && <span className={`pill ${org.status === 'pending' ? '' : 'off'}`}>{org.status}</span>}
        <span>as {org.username}</span>
      </div>
      {(workspace || note || org.endsAt) && (
        <div className="small muted">
          {workspace && <div>{workspace}</div>}
          {note && <div>{note}</div>}
          {org.endsAt && <div>Access until {new Date(org.endsAt).toLocaleString()}</div>}
        </div>
      )}
      <button className="block" onClick={onOpen} disabled={busy !== null || !active}>
        {opening ? 'Opening…' : 'Open in Salesforce'}
      </button>
    </div>
  );
}
